import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Code2, ExternalLink, Mail, UserPlus } from "lucide-react";
import { useAllIntegrations, useCurrentUser } from "../../../hooks/QueryHooks";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import { createIntegration } from "../../../services/integrationEndpoint";
import { CompanyLogo, getDomainFromCompany } from "../../../components/ui/CompanyLogo";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import notify from "../../../utils/notify";
import InviteTeam from "./components/InviteTeam";

const AddIntegration = () => {
  const selectedWorkspace: any = useSelectWorkspace();
  const [segmentStatus, setSegmentStatus] = useState<string>("Connect");
  const { data: integrations, mutate }: any = useAllIntegrations({
    workspaceId: selectedWorkspace?.data?.id,
    enabled: !!selectedWorkspace?.data?.id,
    onError: () => {
      notify({
        heading: "Failed to fetch Integrations",
        messageList: ["Please try again later or contact support to help you resolve this issue"],
        type: "failure",
      });
    },
  });
  const [nativeStatus, setNativeStatus] = useState<string>("Connect");
  const [nativeBadge, setNativeBadge] = useState<string>("");
  const [segmentBadge, setSegmentBadge] = useState<string>("");
  const [openInvite, setOpenInvite] = useState<boolean>(false);
  const navigate = useNavigate();
  const user: any = useCurrentUser({});

  // Email-first path: launch the guided email-setup wizard, which connects the
  // inbox, captures settings, and flips the create_from_email flag itself.
  const emailFirstClick = () => {
    const slug = selectedWorkspace?.data?.unique_identifier;
    navigate(slug ? `/onboarding/workspace/${slug}/email-setup` : "email-setup");
  };
  const segmentIntegration = useMemo(() => integrations?.filter((item: any) => item?.source_name === "segment_api"), [integrations]);
  const nativeIntegration = useMemo(() => integrations?.filter((item: any) => item?.source_name === "native"), [integrations]);

  const segmentClick = async () => {
    if (!segmentIntegration?.length && user) {
      try {
        await createIntegration({
          workspace_identifier: selectedWorkspace?.data?.unique_identifier,
          source_name: "segment_api",
          workspace_id: selectedWorkspace?.data?.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
        await mutate();
      } catch (err) {
        return;
      }
    } else if (segmentIntegration[0].events_recieved) {
      return;
    }
    navigate("segment-api");
  };

  const apiClick = async () => {
    if (!nativeIntegration?.length) {
      try {
        await createIntegration({
          workspace_identifier: selectedWorkspace?.data?.unique_identifier,
          source_name: "native",
          workspace_id: selectedWorkspace?.data?.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
      } catch (err) {
        return;
      }
    } else if (nativeIntegration[0].events_recieved) {
      return;
    }
    navigate("hyperengage-api");
  };

  useEffect(() => {
    if (segmentIntegration?.length) {
      if (segmentIntegration[0].setup_complete) {
        setSegmentStatus("Completed");
      } else {
        setSegmentStatus("Complete setup");
        setSegmentBadge("Setup pending");
      }
    }
  }, [segmentIntegration]);

  useEffect(() => {
    if (nativeIntegration?.length) {
      if (nativeIntegration[0].setup_complete) {
        setNativeStatus("Completed");
      } else {
        setNativeStatus("Complete setup");
        setNativeBadge("Setup pending");
      }
    }
  }, [nativeIntegration]);

  const nativeCompleted = nativeIntegration?.length && nativeIntegration[0]?.setup_complete;
  const segmentCompleted = segmentIntegration?.length && segmentIntegration[0]?.setup_complete;

  const getButtonLabel = (status: string, completed: boolean) => {
    if (completed) return "Completed";
    if (status === "Complete setup") return "Continue setup";
    return "Connect";
  };

  return (
    <>
      <InviteTeam
        open={openInvite}
        workspaceId={selectedWorkspace?.data?.id}
        setOpen={setOpenInvite}
      />
      <section
        className="flex flex-col rounded-lg border border-[#EEEEEE] p-10 max-w-[600px] w-full bg-white relative"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
      >
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium border"
            style={{ borderColor: "#22C55E", backgroundColor: "#22C55E", color: "#FFFFFF" }}
          >
            <Check className="h-3 w-3" />
          </span>
          <span className="text-xs font-medium" style={{ color: "#5C6070" }}>
            Workspace setup
          </span>
          <span className="h-px flex-1" style={{ backgroundColor: "#EEEEEE" }} />
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium"
            style={{ backgroundColor: "#8267E6" }}
          >
            2
          </span>
          <span className="text-xs font-medium" style={{ color: "#181818" }}>
            Connect data
          </span>
        </div>

        {/* Header */}
        <h2 className="text-[15px] font-semibold mb-1" style={{ color: "#181818" }}>
          Connect a data source
        </h2>
        <p className="text-[13px] mb-6" style={{ color: "#5C6070" }}>
          Start with your inbox — Hyperengage builds your accounts and contacts from your email, no code required. Or connect product usage data. You can always add more sources later.
        </p>

        {/* Track A: Simplest setup (email-first) */}
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#8267E6" }}>
          Simplest setup
        </p>

        {/* Integration cards */}
        <div className="flex flex-col gap-3">
          {/* Connect Email - email-first (recommended) */}
          <button
            type="button"
            onClick={emailFirstClick}
            className="group flex items-center gap-4 rounded-lg border p-4 text-left transition-all hover:shadow-sm"
            style={{ borderColor: "#8267E633", backgroundColor: "rgba(130,103,230,0.03)" }}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: "#8267E6" }}>
              <Mail className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium" style={{ color: "#181818" }}>Connect your email</span>
                <span className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: "rgba(130,103,230,0.1)", color: "#8267E6" }}>
                  Recommended
                </span>
              </div>
              <p className="mt-0.5 text-[12px]" style={{ color: "#5C6070" }}>
                Connect Gmail and Hyperengage builds your accounts &amp; contacts automatically — no code, no product data needed.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-[5px] border px-3 py-1 text-[12px] font-medium transition-colors"
                style={{ borderColor: "#6852BD", backgroundColor: "#8267E6", color: "#FFFFFF" }}>
                Set up
                <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </button>

          {/* Track B: Product usage (SDK / Segment) */}
          <p className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#5C6070" }}>
            Product usage
          </p>

          {/* Hyperengage SDK */}
          <button
            type="button"
            onClick={apiClick}
            disabled={!!nativeCompleted}
            className="group flex items-center gap-4 rounded-lg border p-4 text-left transition-all hover:shadow-sm"
            style={{
              borderColor: nativeCompleted ? "#22C55E33" : "#EEEEEE",
              backgroundColor: nativeCompleted ? "rgba(34,197,94,0.03)" : "#FFFFFF",
            }}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: "#8267E6" }}>
              <Code2 className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium" style={{ color: "#181818" }}>Hyperengage SDK</span>
                {nativeBadge && (
                  <Badge variant="statusPill" status={nativeCompleted ? "success" : "warning"}>
                    {nativeCompleted ? "Connected" : nativeBadge}
                  </Badge>
                )}
                {nativeCompleted && (
                  <Badge variant="statusPill" status="success">Connected</Badge>
                )}
              </div>
              <p className="mt-0.5 text-[12px]" style={{ color: "#5C6070" }}>
                Install our lightweight tracking script to send events, identify users, and track accounts.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-[5px] border px-3 py-1 text-[12px] font-medium transition-colors"
                style={{
                  borderColor: "#D0D0D0",
                  backgroundColor: "transparent",
                  color: "#2E3035",
                }}>
                {getButtonLabel(nativeStatus, !!nativeCompleted)}
                {!nativeCompleted && <ArrowRight className="h-3 w-3" />}
              </span>
            </div>
          </button>

          {/* Segment */}
          <button
            type="button"
            onClick={segmentClick}
            disabled={!!segmentCompleted}
            className="group flex items-center gap-4 rounded-lg border p-4 text-left transition-all hover:shadow-sm"
            style={{
              borderColor: segmentCompleted ? "#22C55E33" : "#EEEEEE",
              backgroundColor: segmentCompleted ? "rgba(34,197,94,0.03)" : "#FFFFFF",
            }}
          >
            <CompanyLogo
              domain={getDomainFromCompany("Segment")}
              name="Segment"
              className="h-10 w-10 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium" style={{ color: "#181818" }}>Segment</span>
                {segmentBadge && (
                  <Badge variant="statusPill" status={segmentCompleted ? "success" : "warning"}>
                    {segmentCompleted ? "Connected" : segmentBadge}
                  </Badge>
                )}
                {segmentCompleted && !segmentBadge && (
                  <Badge variant="statusPill" status="success">Connected</Badge>
                )}
              </div>
              <p className="mt-0.5 text-[12px]" style={{ color: "#5C6070" }}>
                Already using Segment? Add Hyperengage as a destination to import your existing data.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-[5px] border px-3 py-1 text-[12px] font-medium transition-colors"
                style={{
                  borderColor: "#D0D0D0",
                  backgroundColor: "transparent",
                  color: "#2E3035",
                }}>
                {getButtonLabel(segmentStatus, !!segmentCompleted)}
                {!segmentCompleted && <ArrowRight className="h-3 w-3" />}
              </span>
            </div>
          </button>
        </div>

        {/* Divider */}
        <div className="h-px mt-8 mb-4" style={{ backgroundColor: "#EEEEEE" }} />

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium transition-colors hover:underline"
              style={{ color: "#8267E6" }}
              onClick={() => { setOpenInvite(true); }}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Invite a team member
            </button>
            <a
              href="https://docs.hyperengage.io/getting-started/start-here"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[12px] transition-colors hover:underline"
              style={{ color: "#5C6070" }}
            >
              View docs
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[12px] text-muted-foreground"
            onClick={() => { navigate("/workspaces"); }}
          >
            Switch workspace
          </Button>
        </div>
      </section>
    </>
  );
};

export default AddIntegration;
